import { defineComponent as _defineComponent } from 'vue';
import { useSearchStore } from "./useSearchStore";
import { useOrderStatus } from "@js/vue/domain/order/composables/useOrderStatus";
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { debounce } from "@js/utilities/helpers";
import { SearchX, ChevronRight, X, Search, Package, MapPin } from 'lucide-vue-next';
import BaseButtonNeutral from "@js/vue/components/ui/BaseButtonNeutral.vue";
import OrderStatusIndicator from "../../../domain/order/components/OrderStatusIndicator.vue";
import { route } from 'ziggy-js';
import { highlightMatch, isAlreadyVisible, hasRelevantMatches } from './search-helpers';
import { useUserStore } from "@js/vue/domain/user/stores/useUserStore";
export default /*@__PURE__*/ _defineComponent({
    __name: 'GlobalSearch',
    props: {
        showResultsInPortal: { type: Boolean, required: false },
        isSpecialStyle: { type: Boolean, required: false },
        isSubheader: { type: Boolean, required: false }
    },
    setup(__props, { expose: __expose }) {
        __expose();
        const props = __props;
        const searchStore = useSearchStore();
        const userStore = useUserStore();
        const showResults = ref(false);
        const isFocused = ref(false);
        const searchWrapper = ref(null);
        const { isStatusVisible, getStatusStyle } = useOrderStatus();
        const typedOrders = computed(() => {
            if (searchStore.context !== 'orders')
                return [];
            return searchStore.results;
        });
        const typedLocations = computed(() => {
            if (searchStore.context !== 'locations')
                return [];
            return searchStore.results;
        });
        const totalResults = computed(() => {
            return searchStore.pagination?.meta?.total ?? 0;
        });
        const hasMoreResults = computed(() => {
            return totalResults.value > searchStore.results.length;
        });
        const dismiss = () => {
            showResults.value = false;
        };
        const performSearch = () => {
            if (searchStore.query.length >= searchStore.minQueryLength) {
                showResults.value = true;
                searchStore.executeSearch(true);
            }
        };
        const handleClear = () => {
            searchStore.clearSearch();
            dismiss();
        };
        const debouncedSearch = debounce(() => { performSearch(); }, 300);
        const handleContextChange = (newContextOrEvent) => {
            if (typeof newContextOrEvent === 'string') {
                searchStore.context = newContextOrEvent; // from select
            }
            else {
                if (newContextOrEvent instanceof MouseEvent) {
                    newContextOrEvent.stopPropagation();
                }
                searchStore.context = searchStore.context === 'orders' ? 'locations' : 'orders'; // from cta
            }
            performSearch();
        };
        const handleClickOutside = (event) => {
            const target = event.target;
            // If the target is no longer in the DOM, it was likely
            // a button removed during a re-render. Don't dismiss.
            if (!target || !document.body.contains(target)) {
                return;
            }
            // if click is outside the search wrap, dismiss.
            if (searchWrapper.value && !searchWrapper.value.contains(target)) {
                dismiss();
            }
        };
        const searchFieldsHint = computed(() => {
            let fields;
            if (searchStore.context === 'orders') {
                fields = ['Order', 'PO', 'Invoice', 'Tracking'];
                if (userStore.isAdmin) {
                    fields.push('Ticket');
                }
            }
            else {
                fields = ['Account', 'City', 'Zip', 'Username'];
                if (userStore.isAdmin) {
                    fields.push('Brand');
                }
            }
            // Join them with commas and add the Oxford 'or'
            return fields.join(', ').replace(/, ([^,]*)$/, ', or $1');
        });
        const viewAllUrl = computed(() => {
            const params = { q: searchStore.query };
            const routeName = searchStore.context === 'locations'
                ? 'account.index.route'
                : 'order.search.results.route';
            try {
                return route(routeName, params);
            }
            catch (e) {
                console.error("Ziggy route error:", e);
                return '#';
            }
        });
        onMounted(() => document.addEventListener('click', handleClickOutside));
        onUnmounted(() => document.removeEventListener('click', handleClickOutside));
        const __returned__ = { props, searchStore, userStore, showResults, isFocused, searchWrapper, isStatusVisible, getStatusStyle, typedOrders, typedLocations, totalResults, hasMoreResults, dismiss, performSearch, handleClear, debouncedSearch, handleContextChange, handleClickOutside, searchFieldsHint, viewAllUrl, get SearchX() { return SearchX; }, get ChevronRight() { return ChevronRight; }, get X() { return X; }, get Search() { return Search; }, get Package() { return Package; }, get MapPin() { return MapPin; }, BaseButtonNeutral, OrderStatusIndicator, get highlightMatch() { return highlightMatch; }, get isAlreadyVisible() { return isAlreadyVisible; }, get hasRelevantMatches() { return hasRelevantMatches; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
